// Zod schemas for API payloads, query parameters, and response envelopes.
// The API validates every request body/query with these; the web app reuses
// the inferred types. Payload objects are `.strict()` so prohibited fields
// (e.g. competitor image URLs, seller data) are rejected rather than ignored.
import { z } from 'zod';
import { AGENT_SCOPES, ALLOWED_CHARACTER_POLICIES, ARTWORK_ASSET_STATUSES, ASSET_ROLES, CONCEPT_STATUSES, EXTERNAL_GEN_METHODS, EXTERNAL_GEN_PROVIDERS, FEASIBILITY_STATUSES, FIELD_LIMITS, IMAGE_ASPECTS, IMAGE_REQUEST_STATUSES, IMAGE_TIERS, INGESTED_IMAGE_STATUSES, IP_RISK_LEVELS, HORIZONTAL_ALIGNMENTS, NORMALIZATION_METHODS, NORMALIZATION_POLICIES, OVERFLOW_BEHAVIORS, PERSONALIZATION_DEF_STATUSES, PERSONALIZATION_INPUT_TYPES, PRICE_BANDS, PRINT_QA_DECISIONS, PRODUCT_PROVIDERS, PRODUCT_TEMPLATE_STATUSES, TEXT_LAYOUT_STATUSES, VERTICAL_ALIGNMENTS, PRODUCT_FORMATS, RESEARCH_STATUSES, SEASONALITY, SIGNAL_LEVELS, SIGNAL_TYPES, } from './domain.js';
/* ------------------------------------------------------------------ *
 * Primitive helpers
 * ------------------------------------------------------------------ */
/** MongoDB ObjectId rendered as a 24-char hex string. */
export const objectIdSchema = z.string().regex(/^[a-f0-9]{24}$/i, 'must be a 24-character hex id');
/**
 * Plain, single-line/multiline text with no HTML angle brackets. Rejecting
 * `<`/`>` keeps stored research free of markup and pasted listing HTML.
 */
export function safeText(max, field = 'value') {
    return z
        .string()
        .trim()
        .min(1, `${field} is required`)
        .max(max, `${field} must be at most ${max} characters`)
        .refine((s) => !/[<>]/.test(s), {
        message: `${field} must not contain HTML angle brackets`,
    });
}
const IMAGE_URL_RE = /\.(png|jpe?g|gif|webp|svg|bmp|tiff?|avif)(?:$|[?#])/i;
/** An http(s) URL that is explicitly not a direct image link. */
export const nonImageUrlSchema = z
    .string()
    .url('must be a valid URL')
    .max(FIELD_LIMITS.url)
    .refine((u) => /^https?:\/\//i.test(u), { message: 'must be an http(s) URL' })
    .refine((u) => !IMAGE_URL_RE.test(u), {
    message: 'direct image URLs are not permitted',
});
/* ------------------------------------------------------------------ *
 * Pagination / query
 * ------------------------------------------------------------------ */
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    sort: z.string().max(40).optional(),
    order: z.enum(['asc', 'desc']).default('desc'),
});
/* ------------------------------------------------------------------ *
 * Research
 * ------------------------------------------------------------------ */
export const researchCreateSchema = z
    .object({
    title: safeText(FIELD_LIMITS.title, 'title'),
    summary: safeText(FIELD_LIMITS.summary, 'summary'),
    signalType: z.enum(SIGNAL_TYPES),
    productFormats: z
        .array(z.enum(PRODUCT_FORMATS))
        .max(FIELD_LIMITS.maxProductFormats)
        .default([]),
    locale: z
        .string()
        .regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'must be a BCP-47 locale like en or en-US')
        .default('en-US'),
    observedAt: z.coerce
        .date()
        .max(new Date(3000, 0), 'observedAt is unreasonably far in the future'),
    sourceUrl: nonImageUrlSchema,
    confidence: z.number().min(0).max(1),
    demandSignal: z.enum(SIGNAL_LEVELS).optional(),
    saturationEstimate: z.enum(SIGNAL_LEVELS).optional(),
    seasonality: z.enum(SEASONALITY).optional(),
    priceBand: z.enum(PRICE_BANDS).optional(),
    feasibilityStatus: z.enum(FEASIBILITY_STATUSES).default('unknown'),
    uncertainty: safeText(FIELD_LIMITS.note, 'uncertainty').optional(),
    policyNotes: safeText(FIELD_LIMITS.note, 'policyNotes').optional(),
    tags: z.array(safeText(FIELD_LIMITS.tag, 'tag')).max(FIELD_LIMITS.maxTags).default([]),
})
    .strict();
export const researchReviewSchema = z
    .object({
    decision: z.enum(['reviewed', 'rejected']),
    notes: safeText(FIELD_LIMITS.note, 'notes').optional(),
})
    .strict();
/** Partial edit of a draft research item (owner only). */
export const researchUpdateSchema = researchCreateSchema.partial();
/** List/filter query with a safe sort allow-list. */
export const researchListQuerySchema = paginationSchema.extend({
    status: z.enum(RESEARCH_STATUSES).optional(),
    signalType: z.enum(SIGNAL_TYPES).optional(),
    tag: z.string().max(FIELD_LIMITS.tag).optional(),
    sourceDomain: z.string().max(255).optional(),
    q: z.string().max(200).optional(),
    sort: z.enum(['observedAt', 'createdAt', 'confidence']).default('observedAt'),
});
/* ------------------------------------------------------------------ *
 * Concepts
 * ------------------------------------------------------------------ */
export const conceptCreateSchema = z
    .object({
    title: safeText(FIELD_LIMITS.title, 'title'),
    consumerNeed: safeText(FIELD_LIMITS.consumerNeed, 'consumerNeed'),
    targetAudience: safeText(FIELD_LIMITS.targetAudience, 'targetAudience'),
    productFormats: z
        .array(z.enum(PRODUCT_FORMATS))
        .min(1, 'at least one product format is required')
        .max(FIELD_LIMITS.maxProductFormats),
    differentiation: safeText(FIELD_LIMITS.differentiation, 'differentiation'),
    researchItemIds: z
        .array(objectIdSchema)
        .min(1, 'at least one reviewed research item is required')
        .max(FIELD_LIMITS.maxResearchRefs),
    policyNotes: safeText(FIELD_LIMITS.note, 'policyNotes').optional(),
})
    .strict();
export const conceptReviewSchema = z
    .object({
    decision: z.enum(['needs_review', 'approved', 'rejected', 'archived']),
    // Required by the service when resolving yellow IP risk; presence is
    // enforced there because it depends on the concept's stored risk level.
    resolutionNote: safeText(FIELD_LIMITS.note, 'resolutionNote').optional(),
})
    .strict();
/** Ask the ideation engine to draft concepts from selected reviewed research. */
export const brainstormSchema = z
    .object({
    researchItemIds: z
        .array(objectIdSchema)
        .min(1, 'select at least one reviewed research item')
        .max(FIELD_LIMITS.maxResearchRefs),
    count: z.coerce.number().int().min(1).max(5).default(3),
})
    .strict();
const scoreComponent = z.number().min(0).max(1);
/** Shape an IdeaGenerator (deterministic or LLM-backed) must produce. */
export const conceptProposalSchema = z
    .object({
    title: safeText(FIELD_LIMITS.title, 'title'),
    consumerNeed: safeText(FIELD_LIMITS.consumerNeed, 'consumerNeed'),
    targetAudience: safeText(FIELD_LIMITS.targetAudience, 'targetAudience'),
    productFormats: z.array(z.enum(PRODUCT_FORMATS)).min(1).max(FIELD_LIMITS.maxProductFormats),
    differentiation: safeText(FIELD_LIMITS.differentiation, 'differentiation'),
    assumptions: z.array(safeText(FIELD_LIMITS.note, 'assumption')).max(10),
    scoreComponents: z.object({
        demand: scoreComponent,
        feasibility: scoreComponent,
        differentiation: scoreComponent,
        margin: scoreComponent,
    }),
})
    .strict();
export const conceptListQuerySchema = paginationSchema.extend({
    status: z.enum(CONCEPT_STATUSES).optional(),
    ipRisk: z.enum(IP_RISK_LEVELS).optional(),
    q: z.string().max(200).optional(),
    sort: z.enum(['updatedAt', 'createdAt']).default('updatedAt'),
});
/* ------------------------------------------------------------------ *
 * Prompt drafts
 * ------------------------------------------------------------------ */
export const printSpecsSchema = z
    .object({
    widthIn: z.number().positive().max(100),
    heightIn: z.number().positive().max(100),
    dpi: z.number().int().min(72).max(1200),
})
    .strict();
export const promptDraftCreateSchema = z
    .object({
    conceptId: objectIdSchema,
    prompt: safeText(FIELD_LIMITS.prompt, 'prompt'),
    negativeConstraints: z
        .array(safeText(FIELD_LIMITS.constraint, 'constraint'))
        .max(FIELD_LIMITS.maxNegativeConstraints)
        .default([]),
    aspectRatio: z.enum(IMAGE_ASPECTS),
    imageModelTier: z.enum(IMAGE_TIERS),
    printSpecs: printSpecsSchema.optional(),
    provenanceNotes: safeText(FIELD_LIMITS.note, 'provenanceNotes').optional(),
})
    .strict();
/* ------------------------------------------------------------------ *
 * Image requests
 * ------------------------------------------------------------------ */
export const imageRequestSubmitSchema = z
    .object({
    promptDraftId: objectIdSchema,
})
    .strict();
export const imageRequestReviewSchema = z
    .object({
    decision: z.enum(['approved', 'rejected']),
    notes: safeText(FIELD_LIMITS.note, 'notes').optional(),
})
    .strict();
export const imageRequestListQuerySchema = paginationSchema.extend({
    status: z.enum(IMAGE_REQUEST_STATUSES).optional(),
    sort: z.enum(['createdAt', 'updatedAt']).default('createdAt'),
});
/* ------------------------------------------------------------------ *
 * Assets
 * ------------------------------------------------------------------ */
export const assetReviewSchema = z
    .object({
    decision: z.enum(['reviewed', 'rejected']),
    notes: safeText(FIELD_LIMITS.note, 'notes').optional(),
})
    .strict();
export const assetListQuerySchema = paginationSchema.extend({
    reviewStatus: z.enum(['pending', 'reviewed', 'rejected']).optional(),
    sort: z.enum(['createdAt']).default('createdAt'),
});
/* ------------------------------------------------------------------ *
 * Ingested images
 * ------------------------------------------------------------------ */
export const ingestedImageProvenanceSchema = z
    .object({
    originalFilename: safeText(255, 'originalFilename'),
    note: safeText(FIELD_LIMITS.note, 'note').optional(),
})
    .strict();
export const ingestedImageNormalizeSchema = z
    .object({
    method: z.enum(NORMALIZATION_METHODS),
    tolerance: z.coerce.number().int().min(0).max(255).default(30),
})
    .strict();
export const ingestedImageReviewSchema = z
    .object({
    decision: z.enum(['approved', 'rejected']),
    variant: z.enum(['original', 'processed']).optional(),
    notes: safeText(FIELD_LIMITS.note, 'notes').optional(),
})
    .strict();
export const ingestedImageListQuerySchema = paginationSchema.extend({
    status: z.enum(INGESTED_IMAGE_STATUSES).optional(),
    sort: z.enum(['createdAt']).default('createdAt'),
});
/* ------------------------------------------------------------------ *
 * Artwork provenance & lineage
 *
 * Provenance and edit metadata arrive as multipart/form-data fields alongside
 * an uploaded file, so text values are strings and empty fields must be
 * normalized to `undefined` before validation.
 * ------------------------------------------------------------------ */
/** Optional free text from a form field: '' or whitespace-only ⇒ undefined. */
function optionalText(max, field = 'value') {
    return z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), safeText(max, field).optional());
}
/** Optional date from a form field: '' ⇒ undefined, otherwise coerced. */
const optionalDate = z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), z.coerce.date().max(new Date(3000, 0), 'unreasonably far in the future').optional());
/** Structured external-generation provenance for an uploaded original. */
export const externalGenerationSchema = z
    .object({
    provider: z.enum(EXTERNAL_GEN_PROVIDERS).default('unknown'),
    model: optionalText(FIELD_LIMITS.modelName, 'model'),
    method: z.enum(EXTERNAL_GEN_METHODS).default('unknown'),
    generatedAt: optionalDate,
    generationRunReference: optionalText(FIELD_LIMITS.generationRunReference, 'generationRunReference'),
    revisedPrompt: optionalText(FIELD_LIMITS.prompt, 'revisedPrompt'),
    uncertaintyNote: optionalText(FIELD_LIMITS.uncertaintyNote, 'uncertaintyNote'),
})
    .strict();
/** Body accompanying an uploaded original PNG (links + provenance). */
export const artworkOriginalCreateSchema = externalGenerationSchema
    .extend({
    conceptId: objectIdSchema,
    promptDraftId: objectIdSchema.optional(),
    originalFilename: safeText(FIELD_LIMITS.filename, 'originalFilename'),
    note: optionalText(FIELD_LIMITS.note, 'note'),
})
    .strict();
/** Body accompanying an uploaded, externally edited derivative of a parent node. */
export const artworkEditedCreateSchema = z
    .object({
    editTool: safeText(120, 'editTool'),
    editor: safeText(120, 'editor'),
    editSummary: safeText(FIELD_LIMITS.editSummary, 'editSummary'),
    editedAt: optionalDate,
    originalFilename: safeText(FIELD_LIMITS.filename, 'originalFilename'),
    note: optionalText(FIELD_LIMITS.note, 'note'),
})
    .strict();
/** Normalize a node into a new derivative; identical shape to ingested-image normalize. */
export const artworkNormalizeSchema = ingestedImageNormalizeSchema;
export const artworkReviewSchema = z
    .object({
    decision: z.enum(['approved', 'rejected']),
    notes: optionalText(FIELD_LIMITS.note, 'notes'),
})
    .strict();
export const artworkListQuerySchema = paginationSchema.extend({
    conceptId: objectIdSchema.optional(),
    lineageId: objectIdSchema.optional(),
    role: z.enum(ASSET_ROLES).optional(),
    status: z.enum(ARTWORK_ASSET_STATUSES).optional(),
    sort: z.enum(['createdAt']).default('createdAt'),
});
/* ------------------------------------------------------------------ *
 * Personalization definitions
 * ------------------------------------------------------------------ */
/** Plain text that may be empty (unlike safeText); still rejects angle brackets. */
function plainText(max, field = 'value') {
    return z
        .string()
        .max(max, `${field} must be at most ${max} characters`)
        .refine((s) => !/[<>]/.test(s), {
        message: `${field} must not contain HTML angle brackets`,
    });
}
export const personalizationFieldSchema = z
    .object({
    fieldKey: z
        .string()
        .regex(/^[a-zA-Z][a-zA-Z0-9]*$/, 'fieldKey must be alphanumeric and start with a letter')
        .max(40),
    label: safeText(120, 'label'),
    helpText: optionalText(500, 'helpText'),
    required: z.boolean().default(false),
    inputType: z.enum(PERSONALIZATION_INPUT_TYPES),
    minLength: z.number().int().min(0).max(1000).default(0),
    maxLength: z.number().int().min(1).max(1000),
    allowedCharacterPolicy: z.enum(ALLOWED_CHARACTER_POLICIES).default('printable_basic'),
    normalizationPolicy: z.enum(NORMALIZATION_POLICIES).default('trim'),
    placeholder: plainText(120, 'placeholder').default(''),
    exampleValue: plainText(120, 'exampleValue').default(''),
    fallbackValue: plainText(120, 'fallbackValue').default(''),
    reviewRequired: z.boolean().default(false),
    active: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(1000),
})
    .strict()
    .refine((f) => f.minLength <= f.maxLength, {
    message: 'minLength must be less than or equal to maxLength',
    path: ['minLength'],
});
export const personalizationDefCreateSchema = z
    .object({
    conceptId: objectIdSchema,
    name: safeText(120, 'name'),
    fields: z
        .array(personalizationFieldSchema)
        .min(1, 'at least one field is required')
        .max(20)
        .refine((fields) => new Set(fields.map((f) => f.fieldKey)).size === fields.length, {
        message: 'fieldKey values must be unique',
    }),
})
    .strict();
export const personalizationDefReviewSchema = z
    .object({
    decision: z.enum(['approved', 'archived']),
    notes: optionalText(FIELD_LIMITS.note, 'notes'),
})
    .strict();
export const personalizationDefListQuerySchema = paginationSchema.extend({
    conceptId: objectIdSchema.optional(),
    status: z.enum(PERSONALIZATION_DEF_STATUSES).optional(),
    sort: z.enum(['createdAt']).default('createdAt'),
});
/* ------------------------------------------------------------------ *
 * Text layouts
 * ------------------------------------------------------------------ */
export const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb hex color');
const boxSchema = z
    .object({
    x: z.number().int().min(0).max(20000),
    y: z.number().int().min(0).max(20000),
    width: z.number().int().min(1).max(20000),
    height: z.number().int().min(1).max(20000),
})
    .strict();
export const textLayerSchema = z
    .object({
    fieldKey: z
        .string()
        .regex(/^[a-zA-Z][a-zA-Z0-9]*$/)
        .max(40),
    box: boxSchema,
    horizontalAlignment: z.enum(HORIZONTAL_ALIGNMENTS).default('center'),
    verticalAlignment: z.enum(VERTICAL_ALIGNMENTS).default('middle'),
    fontId: z.string().min(1).max(60),
    fontSize: z.number().int().min(4).max(2000),
    minFontSize: z.number().int().min(4).max(2000),
    maxFontSize: z.number().int().min(4).max(2000),
    lineHeight: z.number().min(0.5).max(4).default(1.2),
    letterSpacing: z.number().min(-50).max(200).default(0),
    color: hexColorSchema,
    outlineColor: hexColorSchema.nullable().default(null),
    outlineWidth: z.number().min(0).max(50).default(0),
    overflowBehavior: z.enum(OVERFLOW_BEHAVIORS),
    maxLines: z.number().int().min(1).max(10).default(1),
    zIndex: z.number().int().min(0).max(1000).default(0),
    // For optional fields (e.g. foundedYear): when false, the layer is hidden
    // if the value is empty rather than rendering nothing at a reserved spot.
    visibleWhenEmpty: z.boolean().default(true),
})
    .strict()
    .refine((l) => l.minFontSize <= l.fontSize && l.fontSize <= l.maxFontSize, {
    message: 'require minFontSize <= fontSize <= maxFontSize',
    path: ['fontSize'],
});
export const textLayoutCreateSchema = z
    .object({
    conceptId: objectIdSchema,
    personalizationDefId: objectIdSchema,
    name: safeText(120, 'name'),
    canvas: z
        .object({
        width: z.number().int().min(1).max(20000),
        height: z.number().int().min(1).max(20000),
    })
        .strict(),
    layers: z
        .array(textLayerSchema)
        .min(1, 'at least one layer is required')
        .max(20)
        .refine((layers) => new Set(layers.map((l) => l.fieldKey)).size === layers.length, {
        message: 'each fieldKey may appear in at most one layer',
    }),
})
    .strict();
export const textLayoutReviewSchema = z
    .object({
    decision: z.enum(['approved', 'archived']),
    notes: optionalText(FIELD_LIMITS.note, 'notes'),
})
    .strict();
export const textLayoutListQuerySchema = paginationSchema.extend({
    conceptId: objectIdSchema.optional(),
    personalizationDefId: objectIdSchema.optional(),
    status: z.enum(TEXT_LAYOUT_STATUSES).optional(),
    sort: z.enum(['createdAt']).default('createdAt'),
});
/** Render a preview of a layout onto a base artwork asset. */
export const textLayoutRenderSchema = z
    .object({
    baseAssetId: objectIdSchema,
    useExampleValues: z.boolean().default(false),
    values: z.record(z.string().max(400)).optional(),
    backgroundColor: hexColorSchema.optional(),
})
    .strict();
/* ------------------------------------------------------------------ *
 * Product templates & print QA
 * ------------------------------------------------------------------ */
export const productTemplateCreateSchema = z
    .object({
    conceptId: objectIdSchema,
    name: safeText(120, 'name'),
    provider: z.enum(PRODUCT_PROVIDERS),
    productFamily: safeText(60, 'productFamily'),
    blueprintId: safeText(120, 'blueprintId'),
    providerTemplateVersion: safeText(60, 'providerTemplateVersion'),
    retrievedAt: z.coerce.date().max(new Date(3000, 0), 'unreasonably far in the future'),
    printableArea: boxSchema,
    safeArea: boxSchema,
    bleedPx: z.number().int().min(0).max(2000).default(0),
    handleZones: z.array(boxSchema).max(8).default([]),
    blankColor: hexColorSchema,
    outputDimensions: z
        .object({
        width: z.number().int().min(1).max(20000),
        height: z.number().int().min(1).max(20000),
    })
        .strict(),
    baseArtAssetId: objectIdSchema.optional(),
    textLayoutId: objectIdSchema.optional(),
    // No feasibility/pricing may be implied until an owner attaches a dated snapshot.
    feasibilityConfirmed: z.boolean().default(false),
    feasibilityNote: optionalText(500, 'feasibilityNote'),
    feasibilitySnapshotAt: optionalDate,
})
    .strict();
export const productTemplateReviewSchema = z
    .object({
    decision: z.enum(['approved', 'archived']),
    notes: optionalText(FIELD_LIMITS.note, 'notes'),
})
    .strict();
export const productTemplateListQuerySchema = paginationSchema.extend({
    conceptId: objectIdSchema.optional(),
    status: z.enum(PRODUCT_TEMPLATE_STATUSES).optional(),
    sort: z.enum(['createdAt']).default('createdAt'),
});
/** Promote a rendered preview to a production asset against a template + QA. */
export const productionPromoteSchema = z
    .object({
    sourceAssetId: objectIdSchema,
    textLayoutId: objectIdSchema,
    decision: z.enum(PRINT_QA_DECISIONS),
    notes: optionalText(FIELD_LIMITS.note, 'notes'),
})
    .strict();
/* ------------------------------------------------------------------ *
 * Auth
 * ------------------------------------------------------------------ */
export const bootstrapSchema = z
    .object({
    email: z.string().email().max(320),
    password: z.string().min(12, 'password must be at least 12 characters').max(200),
})
    .strict();
export const loginSchema = z
    .object({
    email: z.string().email().max(320),
    password: z.string().min(1).max(200),
})
    .strict();
/* ------------------------------------------------------------------ *
 * API keys
 * ------------------------------------------------------------------ */
export const apiKeyCreateSchema = z
    .object({
    name: safeText(120, 'name'),
    scopes: z
        .array(z.enum(AGENT_SCOPES))
        .min(1, 'at least one scope is required')
        .max(AGENT_SCOPES.length),
})
    .strict();
//# sourceMappingURL=schemas.js.map